import type { TopicNode } from '../types';
import { classifyTopic } from './classifyTopic';

export interface TopicStatInput {
  topic: string;
  count: number;
  lastSeen: number;
}

interface MutableNode {
  name: string;
  path: string;
  count: number;
  leaf: boolean;
  lastSeen: number;
  children: Map<string, MutableNode>;
}

function emptyNode(name: string, path: string): MutableNode {
  return { name, path, count: 0, leaf: false, lastSeen: 0, children: new Map() };
}

function finalize(node: MutableNode, liveWindowMs: number, now: number): TopicNode {
  const children = [...node.children.values()]
    .map((c) => finalize(c, liveWindowMs, now))
    .sort((a, b) => a.name.localeCompare(b.name));
  const live = node.leaf && now - node.lastSeen <= liveWindowMs;
  // A node is "empty" (subscribed but silent) when nothing has ever landed
  // under it. Real topics only enter the store on a message, so count > 0;
  // seeded subscription placeholders carry count 0.
  const empty = node.count === 0;
  const base: TopicNode = {
    name: node.name,
    path: node.path,
    count: node.count,
    leaf: node.leaf,
    live,
    empty,
    children,
  };
  return node.leaf ? { ...base, badge: classifyTopic(node.path) } : base;
}

/** Build a nested topic tree from a flat list of topic stats. Branch counts
 *  are the sum of descendant leaf counts. A leaf is `live` if its `lastSeen`
 *  is within `liveWindowMs` of `now`. */
export function buildTopicTree(
  stats: TopicStatInput[],
  liveWindowMs: number,
  now: number,
): TopicNode[] {
  const roots = new Map<string, MutableNode>();

  for (const stat of stats) {
    const parts = stat.topic.split('/');
    let level = roots;
    let pathAcc = '';
    parts.forEach((part, i) => {
      pathAcc = i === 0 ? part : `${pathAcc}/${part}`;
      let node = level.get(part);
      if (!node) {
        node = emptyNode(part, pathAcc);
        level.set(part, node);
      }
      node.count += stat.count;
      const isLeaf = i === parts.length - 1;
      if (isLeaf) {
        node.leaf = true;
        node.lastSeen = Math.max(node.lastSeen, stat.lastSeen);
      }
      level = node.children;
    });
  }

  return [...roots.values()]
    .map((n) => finalize(n, liveWindowMs, now))
    .sort((a, b) => a.name.localeCompare(b.name));
}
